import React from 'react'
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap'
import intl from 'react-intl-universal'
import { useSelector, useDispatch } from 'react-redux'

import { useSettings } from '~/reactapp/App/SettingsContext'
import { mutatePicklist } from '~/redux/picklistsReducer'
import { setStoryAns } from '~/redux/storyData'
import { errorAction, successAction } from '~/redux/commonActions'
import denormalizer from '~/reactapp/story/util/denormalizer'
import { EllipsisReduxStore } from '~/types'

// eslint-disable-next-line @typescript-eslint/no-var-requires
const styles = require('./PaywallStatusConfirmModal.styles.scss')

export default function PaywallStatusConfirmModal() {
  const dispatch = useDispatch()
  const picklistsData = useSelector(
    (state: EllipsisReduxStore) => state.picklists.data
  )
  const circulations = useSelector(
    (state: EllipsisReduxStore) => state.storyData.circulations
  )
  const story = useSelector(
    (state: EllipsisReduxStore) => state.storyData.story
  )

  const [proposedPaywallStatusId, setProposedPaywallStatusId] = React.useState<
    string
  >()

  const settings = useSettings()

  const previousPrimaryWebsiteRef = React.useRef<string>()
  React.useEffect(() => {
    previousPrimaryWebsiteRef.current = story?.canonical_website
  })

  const didUserChangePrimaryWebsite =
    story && story.canonical_website !== previousPrimaryWebsiteRef.current

  const contentRestrictionsPicklist =
    picklistsData?.picklists?.content_restrictions?.picklist ?? []
  const currentPaywallStatusPicklistItem = contentRestrictionsPicklist.find(
    (picklistItem) =>
      picklistItem.id === story?.content_restrictions?.content_code
  )

  const setStoryContentCode = React.useCallback(
    (contentCode: string) => {
      dispatch(
        setStoryAns({
          storyAns: {
            ...story,
            content_restrictions: {
              ...story?.content_restrictions,
              content_code: contentCode,
            },
            isDirty: true,
          },
          isDirty: true,
        })
      )
      dispatch(
        successAction(intl.get('websiteSelection.updatePaywallStatusSuccess'))
      )
    },
    [dispatch, story]
  )

  React.useEffect(() => {
    async function checkPaywallPrompt() {
      const primaryWebsiteCirculation = circulations?.find(
        (circulation) => circulation.website_id === story?.canonical_website
      )

      if (
        settings.updatePaywallStatusBasedOnSite &&
        window.userRolesAuth.story.editStory &&
        primaryWebsiteCirculation?.website_primary_section
      ) {
        try {
          const [denormalizedSection] = await denormalizer([
            primaryWebsiteCirculation.website_primary_section,
          ])

          const sectionContentCode: string | undefined =
            denormalizedSection?.additional_properties?.original?.metadata
              ?.content_code

          if (sectionContentCode) {
            const storyContentCode = story?.content_restrictions?.content_code

            if (!storyContentCode) {
              setStoryContentCode(sectionContentCode)
            } else if (storyContentCode !== sectionContentCode) {
              setProposedPaywallStatusId(sectionContentCode)
            }
          }
        } catch (error) {
          dispatch(
            errorAction({
              message: intl.get('websiteSelection.sectionDenormalizeError'),
            })
          )
        }
      }
    }

    if (didUserChangePrimaryWebsite) {
      checkPaywallPrompt()
    }
  }, [
    circulations,
    didUserChangePrimaryWebsite,
    dispatch,
    setStoryContentCode,
    settings.updatePaywallStatusBasedOnSite,
    story,
  ])

  return (
    <Modal
      wrapClassName="bootstrap4"
      className={styles.modal}
      centered
      isOpen={Boolean(proposedPaywallStatusId)}
      toggle={() => setProposedPaywallStatusId(undefined)}
    >
      <ModalHeader>{intl.get('PaywallStatusConfirmModal.header')}</ModalHeader>
      <ModalBody>
        <p>
          {intl.get('PaywallStatusConfirmModal.description', {
            paywallStatus:
              currentPaywallStatusPicklistItem?.text ?? 'not in picklist items',
          })}
        </p>
        <p>
          {intl.get('PaywallStatusConfirmModal.prompt', {
            paywallStatus: proposedPaywallStatusId ?? '',
          })}
        </p>
      </ModalBody>
      <ModalFooter>
        <Button
          color="secondary"
          outline
          onClick={() => setProposedPaywallStatusId(undefined)}
        >
          {intl.get('PaywallStatusConfirmModal.buttonNo')}
        </Button>
        <Button
          color="primary"
          onClick={() => {
            if (proposedPaywallStatusId) {
              const picklistHasUpdatedPaywallStatus = contentRestrictionsPicklist.some(
                (picklistItem) => picklistItem.id === proposedPaywallStatusId
              )

              if (!picklistHasUpdatedPaywallStatus) {
                dispatch(
                  mutatePicklist('content_restrictions', {
                    active: true,
                    default: false,
                    id: proposedPaywallStatusId,
                    text: proposedPaywallStatusId,
                  })
                )
              }

              setStoryContentCode(proposedPaywallStatusId)
              setProposedPaywallStatusId(undefined)
            }
          }}
        >
          {intl.get('PaywallStatusConfirmModal.buttonYes')}
        </Button>
      </ModalFooter>
    </Modal>
  )
}
